import React, { useEffect } from 'react';

import { useQuery } from 'react-apollo';
import AcademyQuery from './AcademyQuery.gql';

import { styled } from 'linaria/react';

import { theme } from '../Theme';
import Head from '@jetshop/core/components/Head';

import AcademySidebar from './AcademySidebar';
import AcademyGrid from './AcademyGrid';
import AcademyContentRenderer from './AcademyContentRenderer';

import MaxWidth from '../Layout/MaxWidth';

import LoadingPage from '../LoadingPage';
import BackToTop from './BackToTop';
import { useTracking } from '../../utils/tracking/useTracking';
import { PAGE_TYPES } from '../../utils/tracking/constants';

export const AcademyContainer = styled(MaxWidth)`
  display: grid;
  grid-template-columns: 20% 1fr;
  grid-template-rows: auto 40px 1fr;
  grid-template-areas:
    'sidebar content'
    'sidebar grid-header'
    'sidebar grid';
  margin-top: 4rem;
  margin-bottom: 4rem;
  aside {
    grid-area: sidebar;
  }
  > ul {
    margin-left: 2rem;
    width: calc(80% - 2rem);
  }
  ${theme.below.lg} {
    display: flex;
    flex-direction: column;
    aside {
      max-width: 750px;
    }
  }
  ${theme.below.md} {
    flex-direction: column;
    margin-top: 0.5rem;
    margin-bottom: 4rem;
    aside {
      padding-right: 1rem;
    }
    > ul {
      margin-left: 0;
      width: 100%;
    }
  }
`;

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  max-width: 750px;
  margin: 0 0 2rem 2rem;
  min-height: 5px;
  align-items: center;
  ${theme.below.lg} {
    margin: 0;
  }
  .text-content {
    padding: 1rem;
  }
  .contentComponent {
    .image-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      img {
        object-fit: contain !important;
        width: 100% !important;
        height: 100% !important;
      }
    }
    .content-positioner {
      padding: 1rem !important;
      h2 {
        margin-bottom: 1rem;
        margin-top: 0;
        line-height: 1;
      }
    }
  }
`;

const GridHeader = styled('div')`
  margin: 0 0 2rem 3rem;
  grid-area: grid-header;
`;

const GridContainer = styled('div')`
  grid-area: grid;
`;

const AcademyPage = ({ location: { pathname }, match: { path } }) => {
  const { trackPageView } = useTracking();
  const { data, loading, error } = useQuery(AcademyQuery, {
    variables: {
      pathname
    }
  });

  // Track page view
  useEffect(() => {
    if (!loading && data?.route?.object) {
      trackPageView({
        pageType: PAGE_TYPES.HOOKS_ACADEMY,
        pagePath: pathname,
        pageTitle:
          data.route.object.name ||
          (typeof document !== 'undefined' ? document.title : '')
      });
    }
  }, [loading, data?.route?.object, pathname, trackPageView]);

  if (error) return null;
  if (loading) return <LoadingPage />;
  const {
    route: { object }
  } = data;
  const category = object;
  const subcategories = category?.subcategories ?? [];
  let posts = [];
  if (path === pathname) {
    posts = subcategories.flatMap(subCat => subCat?.subcategories ?? []);
  } else {
    posts = subcategories;
  }
  const items = category?.data?.items;
  const categoryName = category?.name;
  return (
    <AcademyContainer>
      <Head data={category.head} />
      <AcademySidebar path={path} pathname={pathname} />
      {items?.length > 0 && (
        <ContentContainer>
          <AcademyContentRenderer content={items} />
        </ContentContainer>
      )}
      {posts?.length > 0 && (
        <>
          <GridHeader>
            <h2>{categoryName}</h2>
          </GridHeader>
          <GridContainer>
            <AcademyGrid posts={posts} category={category} />
          </GridContainer>
        </>
      )}

      <BackToTop />
    </AcademyContainer>
  );
};

export default AcademyPage;
